// src/app/services/order/order.service.ts

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { and, onSnapshot, or, query } from '@angular/fire/firestore';
import { GlobalService } from '../global/global.service';
import { Order } from '../../models/order.model';
import { User } from '../../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  uid!: string | null;
  lastVisible: any;

  ongoingOrders: Order[] = [];
  isOngoingOrder = false;

  private auth = inject(AuthService);
  private api = inject(ApiService);
  private global = inject(GlobalService);

  private _orders = new BehaviorSubject<Order[]>([]);
  get orders() {
    return this._orders.asObservable();
  }

  // Simple caches to avoid repeated document reads
  private restaurantCache = new Map<string, any>();
  private riderCache = new Map<string, any>();

  constructor() {}

  private safeParseOrder(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private refKey(ref: any): string | null {
    // Works for DocumentReference from firebase (has .path)
    if (ref?.path && typeof ref.path === 'string') return ref.path;
    // Fallback
    if (ref?.id) return String(ref.id);
    return null;
  }

  private async hydrateRestaurant(order: any): Promise<void> {
    if (!order?.restaurant) return;

    // If already hydrated
    if (order.restaurant?.name) return;

    const key = this.refKey(order.restaurant);
    if (key && this.restaurantCache.has(key)) {
      order.restaurant = this.restaurantCache.get(key);
      return;
    }

    const docSnap = await this.api.getDocByReference(order.restaurant);
    if (docSnap?.exists()) {
      const data = docSnap.data();
      if (key) this.restaurantCache.set(key, data);
      order.restaurant = data;
    }
  }

  private async hydrateRider(order: any): Promise<void> {
    if (!order?.rider) return;

    // If already hydrated
    if (order.rider?.name) return;

    const key = this.refKey(order.rider);
    if (key && this.riderCache.has(key)) {
      order.rider = this.riderCache.get(key);
      return;
    }

    const docSnap = await this.api.getDocByReference(order.rider);
    if (docSnap?.exists()) {
      const rider: any = docSnap.data();
      rider.uid = docSnap.id;
      if (key) this.riderCache.set(key, rider);
      order.rider = rider;
    }
  }

  async getUid() {
    if (!this.uid) {
      this.uid = await this.auth.getId();
    }
  }

  async getCoupons(restaurant_id: string) {
    try {
      const currentDateString = new Date().toISOString().split('T')[0];
      const dataRef = this.api.collectionFirestoreRef('coupons');

      let q: any;
      if (restaurant_id) {
        q = query(
          dataRef,
          and(
            this.api.whereQuery('expiryDate', '>=', currentDateString),
            or(
              this.api.whereQuery('restaurant_id', '==', null),
              this.api.whereQuery('restaurant_id', '==', restaurant_id)
            )
          )
        );
      } else {
        q = query(dataRef);
      }

      const querySnapshot = await this.api.getDocsByQuery(q);
      return querySnapshot.docs.map((doc: any) => {
        const coupon: any = doc.data();
        coupon.id = doc.id;
        return coupon;
      });
    } catch (e) {
      throw e;
    }
  }

  async getPastOrders(limitCount: number, nextPage?: boolean) {
    try {
      await this.getUid();
      if (!this.uid) return null;

      const dataRef = this.api.collectionFirestoreRef('orders');
      let q: any = query(
        dataRef,
        this.api.whereQuery('uid', '==', this.uid),
        this.api.orderByQuery('created_at', 'desc'),
        this.api.limitQuery(limitCount)
      );

      if (this.lastVisible && nextPage) {
        q = query(q, this.api.startAfterQuery(this.lastVisible));
      }

      const querySnapshot = await this.api.getDocsByQuery(q);
      this.lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] ?? null;

      const rawOrders: any[] = querySnapshot.docs.map((docSnap: any) => {
        const item: any = docSnap.data();
        item.id = docSnap.id;
        item.order = this.safeParseOrder(item.order);
        return item;
      });

      // Enrich deterministically (no async leaks)
      const orders = await this.getOrderData(rawOrders);

      const current = this._orders.value ?? [];
      this._orders.next([...current, ...orders]);

      return orders;
    } catch (e) {
      throw e;
    }
  }

  async placeOrder(param: any) {
    try {
      await this.getUid();
      if (!this.uid) throw new Error('No auth uid');

      const currentDate = new Date();
      const current_datestring = this.global.dateFormat(new Date(), 'yyyy-MM-dd');

      const order = JSON.stringify(param.order);
      const restaurant = this.api.document(`restaurants/${param.restaurant_id}`);
      const user = this.api.document(`users/${this.uid}`);

      const data = {
        ...param,
        address: Object.assign({}, param.address),
        order,
        restaurant,
        uid: this.uid,
        user,
        created_at: currentDate,
        placed_at: current_datestring,
        updated_at: currentDate,
      };

      const orderRef = await this.api.addDocument('orders', data);
      const order_id = await orderRef?.id;

      let currentOrders: Order[] = [];
      currentOrders.push(
        new Order(
          param.address,
          param.restaurant_id,
          user,
          param.order,
          param.total,
          param.grandTotal,
          param.deliveryCharge,
          param.status,
          param.payment_status,
          param.payment_mode,
          param?.coupon ? param.coupon.saved : 0,
          param?.distance,
          currentDate,
          current_datestring!,
          currentDate,
          param?.tax || 0,
          0,
          order_id,
          this.uid!,
          param.instruction,
          param.restaurant,
          param?.coupon,
          param?.payment_id || null
        )
      );

      const existingOrders = this._orders.value ?? [];
      this._orders.next([...currentOrders, ...existingOrders]);

      return currentOrders;
    } catch (e) {
      throw e;
    }
  }

  async updateOrder(order: any, data: any) {
    try {
      const result = await this.api.updateDocument(`orders/${order.id}`, {
        ...data,
        updated_at: new Date(),
      });

      // Customer app may not have permission to update rider user doc — do not crash.
      if (data?.status === 'Cancelled' && order?.rider_id) {
        try {
          await this.api.updateDocument(`users/${order.rider_id}`, { isOrderActive: false });
        } catch (err) {
          console.log('Non-blocking rider release update failed:', err);
        }
      }

      const current = this._orders.value ?? [];
      const index = current.findIndex((x) => x.id === order.id);
      if (index >= 0) {
        const updated = { ...current[index], ...order, ...data };
        const next = [...current];
        next[index] = updated;
        this._orders.next(next);
      }

      return result;
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  async updateRiderRating(rider_id: string, rating: any) {
    try {
      const docSnap: any = await this.api.getDocById(`users/${rider_id}`);
      if (!docSnap.exists()) throw 'No such document!';

      const rider: User = docSnap.data();
      const totalRating = (rider?.totalRating || 0) + 1;

      const new_rating =
        ((rider?.rating || 0) * (rider?.totalRating || 0) + parseInt(rating, 10)) / totalRating;

      const data = { rating: this.global.formatNumber(new_rating, 1), totalRating };
      return await this.api.updateDocument(`users/${rider_id}`, data);
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  getOrder(id: string): Observable<any> {
    const dataRef: any = this.api.document(`orders/${id}`);

    return new Observable<any>((observer) => {
      const unsubscribe = onSnapshot(dataRef, async (docSnap) => {
        const raw: any = docSnap.data();
        if (!raw) {
          observer.next(null);
          return;
        }

        const data: any = {
          ...raw,
          id: docSnap.id,
          order: this.safeParseOrder(raw.order),
        };

        try {
          await this.hydrateRestaurant(data);
          await this.hydrateRider(data);

          // Sync with cached list (immutable update)
          const current = this._orders.value ?? [];
          const index = current.findIndex((x) => x.id === data.id);
          if (index >= 0) {
            const next = [...current];
            next[index] = data;
            this._orders.next(next);
          }
        } catch (error) {
          console.error('Error enriching order:', error);
        }

        observer.next(data);
      });

      return () => unsubscribe();
    });
  }

  liveTrackRider(rider_id: string) {
    const dataRef: any = this.api.document(`users/${rider_id}`);

    return new Observable<any>((observer) => {
      const unsubscribe = onSnapshot(dataRef, (docSnap) => {
        observer.next(docSnap.data());
      });

      return () => unsubscribe();
    });
  }

  getOngoingOrders(): Observable<Order[]> {
    return new Observable((observer) => {
      // Make sure uid exists; if not, emit empty and keep safe
      const uid = this.uid;
      if (!uid) {
        observer.next([]);
        return () => {};
      }

      const dataRef = this.api.collectionFirestoreRef('orders');
      const status = ['Created', 'Ongoing', 'Picked', 'Accepted'];

      const q: any = query(
        dataRef,
        this.api.whereQuery('uid', '==', uid),
        this.api.whereQuery('status', 'in', status)
      );

      const unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          const changes = querySnapshot.docChanges();

          this.isOngoingOrder = this.ongoingOrders.length === 0;

          let updated_order = false;
          let indexInOrders = -1;

          const allOrders = this._orders.value ?? [];

          for (const change of changes) {
            const doc: any = change.doc.data();
            const order: any = {
              ...doc,
              id: change.doc.id,
              order: this.safeParseOrder(doc.order),
            };

            if (change.type === 'added') {
              if (this.isOngoingOrder) {
                this.ongoingOrders.push(order);
              } else {
                const exists = this.ongoingOrders.find((x) => x.id === order.id);
                if (!exists) this.ongoingOrders.push(order);
              }
            } else if (change.type === 'modified') {
              const idx = this.ongoingOrders.findIndex((x) => x.id === order.id);
              if (idx >= 0) this.ongoingOrders[idx] = order;

              indexInOrders = allOrders.findIndex((x) => x.id === order.id);
              if (indexInOrders >= 0) {
                const next = [...allOrders];
                next[indexInOrders] = order;
                this._orders.next(next);
              }
            }

            if (
              change.type === 'removed' ||
              order?.status === 'Cancelled' ||
              order?.status === 'Rejected' ||
              order?.status === 'Delivered'
            ) {
              this.ongoingOrders = this.ongoingOrders.filter((x) => x.id !== order.id);
              updated_order = true;

              indexInOrders = allOrders.findIndex((x) => x.id === order.id);
              if (indexInOrders >= 0) {
                const next = [...allOrders];
                next[indexInOrders] = order;
                this._orders.next(next);
              }
            }
          }

          if (!updated_order) {
            this.ongoingOrders = await this.getOrderData(this.ongoingOrders);
          }

          observer.next([...this.ongoingOrders]);
        },
        (error) => observer.error(error)
      );

      return () => unsubscribe();
    });
  }

  async getOrderData(orders: any[]): Promise<Order[]> {
    if (!orders || orders.length === 0) return orders;

    const ordersData = await Promise.all(
      orders.map(async (order) => {
        try {
          await this.hydrateRestaurant(order);
          await this.hydrateRider(order);
          return order;
        } catch (error) {
          console.error('Error fetching order data:', error);
          return null;
        }
      })
    );

    return ordersData.filter((o) => o !== null) as Order[];
  }

  reset() {
    this.uid = null;
    this._orders.next([]);
    this.lastVisible = null;
    this.restaurantCache.clear();
    this.riderCache.clear();
  }

  resetOngoingOrders() {
    this.ongoingOrders = [];
    this.isOngoingOrder = false;
  }
}
